import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

// People timeline live updates. Subscribed to Sites::Chats::ManagerChannel
// for the website. When a new chat message arrives, we:
//   1. Find the matching chat-thread row in the Activity feed by data-chat-id
//   2. Update its preview text + status badge + time-ago
//   3. If this contact is the active chat, flash a subtle highlight
//
// If the chat row doesn't exist yet (e.g. visitor opened a new chat after
// the page rendered), we don't construct a new row — a full refresh covers
// that less-common case. The visible feedback for the much-more-common
// "manager + visitor in active conversation" path is what matters here.
export default class extends Controller {
  static values = {
    websiteId: String,
    contactId: String
  }

  static targets = ["chatRow", "activeStatus"]

  connect() {
    if (!this.websiteIdValue) return
    this.subscription = consumer.subscriptions.create(
      { channel: "Sites::Chats::ManagerChannel", website_id: this.websiteIdValue },
      {
        received: (data) => this.handleEvent(data)
      }
    )
  }

  disconnect() {
    this.subscription?.unsubscribe()
    this.subscription = null
  }

  handleEvent(data) {
    if (!data || data.type !== 'message') return
    const chat = data.chat
    if (!chat) return
    // Only act on chats for this contact.
    if (chat.contact_id && chat.contact_id !== this.contactIdValue) return

    const row = this.chatRowTargets.find((el) => el.dataset.chatId === data.chat_id)
    if (!row) return

    // Update the last-message preview line + bump the time-ago to "just now".
    const previewEl = row.querySelector('[data-people-timeline-target="preview"]')
    if (previewEl && data.message) {
      const senderLabel = (data.message.sender_kind || '').replace(/^./, (c) => c.toUpperCase())
      previewEl.innerHTML = `<span class="font-medium">${senderLabel}:</span> ${this.escape(data.message.body || '')}`
    }
    const timeEl = row.querySelector('[data-people-timeline-target="timestamp"]')
    if (timeEl) timeEl.textContent = 'just now'

    // Refresh the chat status badge (open / snoozed / closed) if status changed.
    if (chat.status) {
      const badge = row.querySelector('[data-people-timeline-target="statusBadge"]')
      if (badge) {
        badge.className = `badge badge-${chat.status === 'open' ? 'success' : 'default'} text-xs`
        badge.textContent = chat.status.replace(/^./, (c) => c.toUpperCase())
      }
    }

    // Flash to draw the eye.
    row.style.transition = 'background-color 1.2s ease'
    row.style.backgroundColor = 'rgba(34, 197, 94, 0.08)'
    setTimeout(() => { row.style.backgroundColor = '' }, 1200)
  }

  escape(s) {
    const d = document.createElement('div')
    d.textContent = s
    return d.innerHTML
  }
}
